import { isNil } from 'lodash';
import { PickByValue } from 'utility-types';

import { useData } from '@bai/data';
import { useUser } from '@bai/features/user';
import { TimesInMilliseconds } from '@bai/utils/types';

/* These typings were generated from the api/settings endpoint response */
export interface SettingsResponse {
  hasWarnings: boolean;
  warnings: unknown[];
  enrollmentAttempts: number;
  features: Features;
}

export interface Features {
  sysPropInstructorLed: boolean;
  sysPropProSightILTAccess: boolean;
  sysPropCourseGroups: boolean;
  sysPropTasks: boolean;
  sysPropIDesign: boolean;
  sysPropDefaultMasteryScore: SysPropDefaultMasteryScore;
  custom4: string;
  enrollmentLimitCount: EnrollmentLimitCount;
  enrollmentLimitDays: EnrollmentLimitDays;
  enrollmentLimitType: EnrollmentLimitType;
  enrollmentLimitRefreshMonth: EnrollmentLimitRefreshMonth;
  enrollmentLimitRefreshDate: EnrollmentLimitRefreshDate;
  'orgProp.timezone': OrgPropTimezone;
  'orgProp.PeerGroup': string;
  sysPropTrainingInsights: boolean;
  sysPropCareerPath: boolean;
  sysPropCareerPathDashboard: boolean;
  sysPropManagersCanSetGoals: string;
  sysPropManagersCanWaiveGoals: string;
  sysPropIconsToShow: string;
  sysPropProxyMode: boolean;
  sysPropRusticiAccess: boolean;
  sysPropDocument: boolean;
  sysPropCustomFolder: string;
  enableLmsPlmIntegration: boolean;
  enableOverdueBanner: boolean;
  enableUniversalHeader: boolean;
  sysPropContentRecommendations: boolean;
  sysPropAdminDashboardUserLimit: number;
}

export type FeatureFlags = PickByValue<Features, boolean>;
export type FeatureFlagNames = keyof FeatureFlags;

export interface SysPropDefaultMasteryScore {
  value: number;
}

export interface EnrollmentLimitCount {
  value: number;
}

export interface EnrollmentLimitDays {
  value: number;
}

export interface EnrollmentLimitType {
  value: string;
}

export interface EnrollmentLimitRefreshMonth {
  value: number;
}

export interface EnrollmentLimitRefreshDate {
  value: number;
}

export interface OrgPropTimezone {
  value: number;
  name: string;
}

export function useSettings() {
  const catalogRegex = /^catalog[.-]/i; // hostname begins with catalog. or catalog- case insensitive

  const isPublic = catalogRegex.test(window.location.hostname);
  const user = useUser();
  // On local env only, scope cache per user so a user switch always fetches fresh settings
  const isLocal = process.env.NODE_ENV === 'development';
  const cacheKey =
    isLocal && user?.id ? `settings-user-${user.id}` : 'settings/';
  // TODO: See if we can cache the response offline and re-hydrate on load
  return useData<SettingsResponse>(
    'settings/',
    {
      enabled: !isPublic && !isNil(user),
      staleTime: TimesInMilliseconds.ONE_HOUR,
    },
    undefined,
    undefined,
    cacheKey,
  );
}

export function useHasFeature<T extends FeatureFlagNames>(
  featureName: T,
): boolean {
  const { data, isLoading, error } = useSettings();
  if (isLoading || !isNil(error) || isNil(data) || isNil(data.features))
    return false;
  return data.features[featureName];
}

export function useTimezone(): OrgPropTimezone {
  const { data, isLoading, error } = useSettings();
  if (isLoading || !isNil(error) || isNil(data) || isNil(data.features))
    return { value: 19, name: 'America/Chicago' };
  return data.features['orgProp.timezone' as keyof Features] as OrgPropTimezone;
}
